import { useOptionalAssignmentService } from 'src/apis/assignment-service/use-optional-assignment-service';
import { useOptionalAuthorizationService } from 'src/apis/authorization-service/use-authorization-service';
import { useOptionalCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useDocumentExchange } from 'src/apis/document-exchange/use-document-exchange-root';
import { useHydrogenFcfsRoot } from 'src/apis/hydrogen-fcfs/use-hydrogen-fcfs-root';
import { useOptionalLngMarketingRoot } from 'src/apis/lng-marketing/use-lng-marketing-root';
import {
  useOptionalOrganisationService,
  type OrganisationServiceRequest,
} from 'src/apis/organisation-service/use-organisation-service';
import { useRegularFcfsRoot } from 'src/apis/regular-fcfs/use-regular-fcfs-root';
import { useRemitReportingEntrypoint } from 'src/apis/remit-reporting/use-remit-reporting-entrypoint';
import { useOptionalStorageServiceRoot } from 'src/apis/storage/use-storage-service-root';
import { useOptionalSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { useOptionalTransparencyReportingAuthorization } from 'src/apis/transparency-reporting/use-transparency-reporting-authorization';
import { useAccounts } from 'src/hooks/use-accounts';
import type { Successful } from 'src/hooks/use-axios';
import { useCognitoUser } from 'src/hooks/use-cognito-user';
import { useDirectFcfsFlags } from 'src/hooks/use-direct-fcfs-flags';
import { useGmdFlags } from 'src/hooks/use-gdm-flags';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useSystemInfo } from 'src/hooks/use-system-info';
import { useIsSnamAdmin } from 'src/pages/snam-bid-collector/use-is-snam-admin';

export function useAppShellData() {
  const cognitoUser = useCognitoUser();
  const accounts = useAccounts();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const [reauthenticate] = useReauthenticate();

  // service data that is already loaded
  const directFcfsService = useDirectFcfsFlags();
  const documentExchange = useDocumentExchange();
  const systemInfo = useSystemInfo();
  const gdmFlags = useGmdFlags();

  // services that need to be loaded
  const authorizationService = useOptionalAuthorizationService();
  const organisationService = useOptionalOrganisationService();
  const regularFcfsService = useRegularFcfsRoot();
  const hydrogenFcfsService = useHydrogenFcfsRoot();
  const lngMarketingRoot = useOptionalLngMarketingRoot();
  const assignmentService = useOptionalAssignmentService();
  const remitReportingEntrypoint = useRemitReportingEntrypoint();
  const isSnamAdmin = useIsSnamAdmin();
  const storageServiceRoot = useOptionalStorageServiceRoot();
  const capacityManagement = useOptionalCapacityManagementRoot();
  const transparencyReportingAuthorization =
    useOptionalTransparencyReportingAuthorization();
  const surrenderServiceRoot = useOptionalSurrenderRoot();

  if (
    (authorizationService.response === null &&
      authorizationService.error === null) ||
    (lngMarketingRoot.response === null && lngMarketingRoot.error === null) ||
    (organisationService.response === null &&
      organisationService.error === null) ||
    (assignmentService.response === null && assignmentService.error === null) ||
    !remitReportingEntrypoint.loaded ||
    !regularFcfsService.loaded ||
    (hydrogenFcfsService && !hydrogenFcfsService.loaded) ||
    isSnamAdmin === undefined ||
    (storageServiceRoot.response === null &&
      storageServiceRoot.error === null) ||
    (capacityManagement.response === null &&
      capacityManagement.error === null) ||
    (transparencyReportingAuthorization.response === null &&
      transparencyReportingAuthorization.error === null) ||
    (surrenderServiceRoot.response === null &&
      surrenderServiceRoot.error === null)
  )
    return null;

  return {
    monolithUser,
    cognitoUser,
    isAuthenticated: Boolean(cognitoUser),
    reauthenticate,
    isSnamAdmin,
    documentExchange,
    systemInfo,
    accounts,
    authorizationService:
      authorizationService.response === null ? undefined : authorizationService,
    organisationService:
      organisationService.response === null
        ? undefined
        : (organisationService as Successful<OrganisationServiceRequest>),
    assignmentService:
      assignmentService.response === null ? undefined : assignmentService,
    capacityManagementService: capacityManagement.response?.data,
    regularFcfsService: regularFcfsService.response?.data,
    directFcfsService: directFcfsService.data,
    hydrogenFcfsService: hydrogenFcfsService?.response?.data,
    remitReportingService: remitReportingEntrypoint.response?.data,
    transparencyReportingAuthorization:
      transparencyReportingAuthorization.response?.data,
    surrenderServiceRoot: surrenderServiceRoot.response?.data,
    gdmFlags,
    lngMarketingRoot: lngMarketingRoot.response ? lngMarketingRoot : undefined,
    storageServiceRoot: storageServiceRoot.response
      ? storageServiceRoot
      : undefined,
  };
}

export type AppShellData = ReturnType<typeof useAppShellData>;

export type LoadedAppShellData = Exclude<AppShellData, null>;
